import React, { useEffect, useRef, useState } from 'react';
import { StyleProp, TextStyle, View, ViewStyle, Image, TouchableOpacity, ActivityIndicator, ImageStyle, Pressable } from 'react-native';
import { faFile, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faCloudArrowUp, faXmark } from '@fortawesome/free-solid-svg-icons';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Controller, FieldValues, RegisterOptions, UseFormReturn, useWatch } from 'react-hook-form';
import { translate } from '../services/translate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WindowInformation } from '../services/window-information';
import Tooltip from './Tooltip';

export type FileFieldValue = {
    file: File | undefined;
    [key: string]: unknown;
};

type FileType = 'image/png' | 'image/jpg' | 'image/jpeg' | 'application/pdf';

interface FormFileUploaderProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    label?: string | React.ReactNode;
    labelStyle?: StyleProp<TextStyle>;
    labelFontSize?: number;
    disabled?: boolean;
    required?: boolean;
    maxFileSize?: number; // MB
    multiple?: boolean;
    maxFiles?: number;
    // styles
    viewStyle?: StyleProp<ViewStyle>;
    uploadAreaStyle?: StyleProp<ViewStyle>;
    previewStyle?: StyleProp<ViewStyle>;
    previewImageStyle?: StyleProp<ImageStyle>;
    previewsContainerStyle?: StyleProp<ViewStyle>;
    uploadComponentStyle?: React.CSSProperties;
    // texts
    uploadText?: string;
    dragDropText?: string;
    supportedFormatsText?: string;
    recommendedSizeText?: string;

    showPreview?: boolean;
    icon?: IconDefinition;

    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    onUpload?: (file: File | File[]) => Promise<void>;
    removeButtonStyle?: StyleProp<ViewStyle>;
    allowedFileTypes?: FileType[];
    actions?: React.ReactNode;
    defaultValues?: FileFieldValue | FileFieldValue[];
    emptyObjectValues?: FileFieldValue | FileFieldValue[];
    allowDownload?: boolean;
    uploadPosition?: 'top' | 'bottom';
}

export default function FormFileUploader({
    form,
    fieldName,
    label,
    labelStyle,
    labelFontSize,
    disabled = false,
    required,
    maxFileSize,
    multiple = false,
    maxFiles,
    viewStyle,
    uploadAreaStyle,
    previewStyle,
    previewImageStyle,
    previewsContainerStyle,
    uploadComponentStyle,
    uploadText,
    dragDropText,
    supportedFormatsText,
    recommendedSizeText,
    showPreview = true,
    icon,
    customValidations,
    onUpload,
    removeButtonStyle,
    allowedFileTypes,
    actions,
    defaultValues,
    emptyObjectValues,
    allowDownload,
    uploadPosition = 'top',
}: FormFileUploaderProps) {
    const windowInfo = WindowInformation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { styles, theme } = useStyles(stylesheet);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showDownloadTooltip, setShowDownloadTooltip] = useState<boolean>(false);

    const tooltipRef = useRef<HTMLDivElement>(null);
    const [tooltipHeight, setTooltipHeight] = useState<number>(0);
    const [tooltipWidth, setTooltipWidth] = useState<number>(0);
    const [previewWidth, setPreviewWidth] = useState<number>(0);

    const fieldValue = useWatch({
        control: form.control,
        name: fieldName
    }) as FileFieldValue | FileFieldValue[];

    useEffect(() => {
        if (tooltipRef.current) {
            setTooltipHeight(tooltipRef.current.offsetHeight + 12); // 12 is for emulate padding
            setTooltipWidth(tooltipRef.current.offsetWidth);
        }
    }, [fieldValue]);

    function handleUploadClick() {
        if (fileInputRef.current && !disabled) {
            fileInputRef.current.click();
        }
    }

    function fileRequired(value: FileFieldValue | FileFieldValue[]): string | true {
        if (!required) {
            return true;
        }

        if (multiple) {
            const multiValue = value as FileFieldValue[];
            if (!multiValue || multiValue.length === 0) {
                return translate('required_field');
            }
        } else {
            const singleValue = value as FileFieldValue;
            if (!singleValue?.file) {
                return translate('required_field');
            }
        }

        return true;
    }

    function validateFile(file: File): string | true {
        if (!file) {
            return true;
        }

        if (maxFileSize && file.size > maxFileSize * 1024 * 1024) {
            return translate('file_size_exceeded', { maxSize: maxFileSize });
        }
        return true;
    }

    function validateFileType(file: File): string | true {
        if (!file || !allowedFileTypes || allowedFileTypes.length === 0) {
            return true;
        }

        if (!allowedFileTypes.includes(file.type as FileType)) {
            return translate('invalid_file_type');
        }
        return true;
    }

    function validateFiles(files: File[]): string | true {
        for (const file of files) {
            const sizeValidation = validateFile(file);
            if (sizeValidation !== true) {
                return sizeValidation;
            }

            const typeValidation = validateFileType(file);
            if (typeValidation !== true) {
                return typeValidation;
            }
        }
        return true;
    }

    async function handleFileChange(newFiles: File | File[] | null) {
        const currentValues = form.getValues(fieldName) as FileFieldValue | FileFieldValue[];

        if (newFiles) {
            const filesArray = Array.isArray(newFiles) ? newFiles : [newFiles];

            const validation = validateFiles(filesArray);
            if (validation !== true) {
                form.setError(fieldName, {
                    type: 'manual',
                    message: validation
                });
                return;
            }

            form.clearErrors(fieldName);
            setIsUploading(true);

            try {
                if (onUpload) {
                    await onUpload(multiple ? filesArray : filesArray[0]);
                }

                if (multiple) {
                    const existingFiles = Array.isArray(currentValues)
                        ? currentValues.map(item => item.file).filter((f): f is File => f !== undefined)
                        : [];
                    const allFiles = [...existingFiles, ...filesArray];

                    const sortedFiles = allFiles.sort((a, b) => {
                        const aIsImage = isImageFile(a);
                        const bIsImage = isImageFile(b);

                        if (aIsImage === bIsImage) {
                            return 0;
                        }
                        return aIsImage ? 1 : -1;
                    });

                    const limitedFiles = maxFiles ? sortedFiles.slice(0, maxFiles) : sortedFiles;

                    const newValue: FileFieldValue[] = limitedFiles.map(file => ({
                        ...(Array.isArray(defaultValues) ? defaultValues[0] : {}),
                        file
                    }));

                    form.setValue(fieldName, newValue, {
                        shouldValidate: true,
                        shouldDirty: true
                    });
                } else {
                    form.setValue(fieldName, {
                        ...(defaultValues ? defaultValues : currentValues),
                        file: filesArray[0],
                    }, {
                        shouldValidate: true,
                        shouldDirty: true
                    });
                }
            } catch (error) {
                console.error('Upload error:', error);
            } finally {
                setIsUploading(false);
            }
        } else {
            setPreviewUrls([]);
            form.setValue(fieldName, emptyObjectValues || (multiple ? { files: [] } : null), {
                shouldValidate: true,
                shouldDirty: true
            });
        }
    }

    function handleRemoveFile(index: number) {
        if (!multiple) {
            return;
        }

        const currentFiles = (fieldValue as FileFieldValue[]) || [];
        const updatedFiles = currentFiles.filter((_, i) => i !== index);

        form.setValue(fieldName, updatedFiles, {
            shouldValidate: true,
            shouldDirty: true
        });

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    function handleDragOver(e: React.DragEvent) {
        e.preventDefault();
        if (!disabled) {
            setIsDragOver(true);
        }
    }

    function handleDragLeave(e: React.DragEvent) {
        e.preventDefault();
        setIsDragOver(false);
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        setIsDragOver(false);

        if (disabled) {
            return;
        }

        const files = Array.from(e.dataTransfer.files);
        if (files && files.length > 0) {
            const filesToProcess = multiple ? files.slice(0, maxFiles) : [files[0]];

            const validation = validateFiles(filesToProcess);
            if (validation !== true) {
                form.setError(fieldName, {
                    type: 'manual',
                    message: validation
                });
                return;
            }

            handleFileChange(multiple ? filesToProcess : filesToProcess[0]);
        }
    }

    const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

    if (required) {
        rules.required = translate('required_field');

        rules.validate = {
            fileRequired: (val: FileFieldValue | FileFieldValue[]) => fileRequired(val),
        };
    }

    rules.validate = {
        ...rules.validate,
        fileSize: (val: FileFieldValue | FileFieldValue[]) => {
            if (multiple) {
                const files = Array.isArray(val)
                    ? val.map(item => item.file).filter((f): f is File => f !== undefined)
                    : [];
                return validateFiles(files);

            } else {
                const file = (val as FileFieldValue)?.file;
                return file ? validateFile(file) : true;
            }
        },
        fileType: (val: FileFieldValue | FileFieldValue[]) => {
            if (multiple) {
                const files = Array.isArray(val)
                    ? val.map(item => item.file).filter((f): f is File => f !== undefined)
                    : [];
                return validateFiles(files);
            } else {
                const file = (val as FileFieldValue)?.file;
                return file ? validateFileType(file) : true;
            }
        }
    };
    if (customValidations) {
        rules.validate = {
            ...rules.validate,
            ...customValidations,
        };
    }

    const isImageFile = (file?: File) => !!file && file.type.startsWith('image/');
    useEffect(() => {
        if (multiple) {
            const files = Array.isArray(fieldValue)
                ? fieldValue.map(item => item.file).filter((f): f is File => f !== undefined)
                : [];

            if (files.length === 0) {
                setPreviewUrls([]);
                return;
            }

            const urls = files.map(file => isImageFile(file) ? URL.createObjectURL(file) : '');
            setPreviewUrls(urls);

            return () => urls.forEach(url => URL.revokeObjectURL(url));
        } else {
            const file = (fieldValue as FileFieldValue)?.file;
            if (!file || !isImageFile(file)) {
                setPreviewUrls([]);
                return;
            }

            const url = URL.createObjectURL(file);
            setPreviewUrls([url]);

            return () => URL.revokeObjectURL(url);
        }
    }, [fieldValue, multiple]);

    function renderSingleFilePreview(file: File, index: number = 0) {
        const previewUrl = previewUrls[index];

        return (
            <View key={index} style={[styles.previewContainer, previewStyle]}>
                <Tooltip
                    ref={tooltipRef}
                    useSimpleBox
                    style={{
                        display: allowDownload ? 'flex' : 'none',
                        width: 'auto',
                        color: theme.colors.white['500'],
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 16,
                        paddingRight: 16,
                        borderRadius: 12,
                        justifyContent: 'center',
                        zIndex: 9999,
                        fontSize: 12,
                        fontWeight: 500,
                        top: -tooltipHeight,
                        left: (previewWidth / 2) - (tooltipWidth / 2),
                        background: theme.colors.neutral['500'],
                    }}
                    tooltipContent={translate('download')}
                    tooltipDirection='top'
                >
                    <Pressable
                        onLayout={(e) => setPreviewWidth(e.nativeEvent.layout.width)}
                        onHoverIn={() => index === 0 && setShowDownloadTooltip(true)}
                        onHoverOut={() => index === 0 && setShowDownloadTooltip(false)}
                        style={[allowDownload ? { cursor: 'pointer' } as ViewStyle : { cursor: 'auto' } as ViewStyle]}
                        onPress={() => {
                            if (!allowDownload || !file) {
                                return;
                            }

                            const url = URL.createObjectURL(file);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = file.name;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                        }}
                    >
                        {isImageFile(file) && previewUrl
                            ? <Image
                                source={{ uri: previewUrl }}
                                style={[styles.previewImage, previewImageStyle]}
                                resizeMode="contain"
                            />
                            : <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                <FontAwesomeIcon
                                    icon={faFile}
                                    fontSize={32}
                                    color={theme.colors.neutral['300']}
                                />
                                <MyAppText style={styles.fileName}>
                                    {file.name}
                                </MyAppText>
                            </View>
                        }
                    </Pressable>
                </Tooltip>
                {actions
                    ? actions
                    : !disabled && <TouchableOpacity
                        style={[styles.removeButton, removeButtonStyle]}
                        onPress={() => {
                            if (multiple) {
                                handleRemoveFile(index);
                            } else {
                                setPreviewUrls([]);
                                form.setValue(fieldName, null);
                                if (fileInputRef.current) {
                                    fileInputRef.current.value = '';
                                }
                            }
                        }}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon
                            icon={faXmark}
                            fontSize={16}
                            color={theme.colors.errors['300']}
                        />
                    </TouchableOpacity>
                }
            </View>
        );
    }

    const hasFiles = multiple
        ? (Array.isArray(fieldValue) && fieldValue.length > 0)
        : !!(fieldValue as FileFieldValue)?.file;

    function uploadFileComponent() {
        return <div
            onClick={handleUploadClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e)}
            style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                pointerEvents: disabled ? 'none' : 'auto',
                ...uploadComponentStyle
            }}
        >
            <View style={[
                styles.dropzone,
                isDragOver && styles.dropzoneActive,
                disabled && styles.dropzoneDisabled,
                uploadAreaStyle
            ]}>
                <FontAwesomeIcon
                    icon={icon || faCloudArrowUp}
                    fontSize={32}
                    color={disabled ? theme.colors.neutral['400'] : theme.colors.primary['500']}
                />
                <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 3, alignItems: 'baseline' }}>
                    <MyAppText style={styles.uploadText}>
                        {uploadText || translate('clickToUpload')}
                    </MyAppText>
                    <MyAppText style={styles.dragDropText}>
                        {dragDropText || translate('orDragAndDrop')}
                    </MyAppText>
                </View>
                {supportedFormatsText && (
                    <MyAppText style={styles.supportedText}>
                        {supportedFormatsText}
                    </MyAppText>
                )}
                {recommendedSizeText && (
                    <MyAppText style={styles.recommendedText}>
                        {recommendedSizeText}
                    </MyAppText>
                )}
                {multiple && maxFiles && !hasFiles && (
                    <MyAppText style={styles.recommendedText}>
                        {translate('maxFiles')}: {maxFiles}
                    </MyAppText>
                )}
                {maxFiles && hasFiles && <MyAppText style={styles.addMoreText}>
                    {translate('addMoreFiles')} ({(fieldValue as FileFieldValue[]).length}/{maxFiles})
                </MyAppText>}
            </View>
        </div>;
    }

    function previewsComponent() {
        if (!showPreview || !hasFiles) {
            return null;
        }

        return (
            <View style={[styles.previewsContainer, showDownloadTooltip && uploadComponentStyle && { zIndex: Number(uploadComponentStyle.zIndex || 0) + 1 }, previewsContainerStyle]}>
                {multiple
                    ? (fieldValue as FileFieldValue[])?.map((item, index) =>
                        item.file ? renderSingleFilePreview(item.file, index) : null
                    )
                    : renderSingleFilePreview((fieldValue as FileFieldValue).file!)
                }
            </View>
        );
    }

    return (
        <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ fieldState }) => (
                <View style={[viewStyle]}>
                    <View style={{ flexDirection: 'row', columnGap: 5 }}>
                        {label && (
                            <>
                                {typeof label === 'string'
                                    ? <MyAppText style={labelStyle ? labelStyle : styles.label(labelFontSize ?? 16)}>
                                        {label}
                                    </MyAppText>
                                    : label
                                }
                            </>
                        )}
                        {required && (
                            <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        )}
                    </View>

                    <input
                        type="file"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const files = e.target.files;
                            if (files && files.length > 0) {
                                const fileArray = Array.from(files);
                                handleFileChange(multiple ? fileArray : fileArray[0]);
                            }
                        }}
                        ref={(input) => (fileInputRef.current = input)}
                        accept={allowedFileTypes?.join(',')}
                        disabled={disabled || isUploading}
                        multiple={multiple}
                    />

                    {isUploading ? (
                        <View style={[styles.loadingContainer, uploadAreaStyle]}>
                            <ActivityIndicator size="large" color={theme.colors.primary['500']} />
                            <MyAppText style={styles.loadingText}>
                                {translate('uploading')}
                            </MyAppText>
                        </View>
                    ) : (
                        <View style={styles.contentContainer}>
                            {uploadPosition === 'bottom' ? (
                                <>
                                    {previewsComponent()}
                                    {hasFiles && multiple && (!maxFiles || (fieldValue as FileFieldValue[])?.length < maxFiles) && uploadFileComponent()}
                                    {!hasFiles && uploadFileComponent()}
                                </>
                            ) : (
                                <>
                                    {hasFiles && multiple && (!maxFiles || (fieldValue as FileFieldValue[])?.length < maxFiles) && uploadFileComponent()}
                                    {!hasFiles && uploadFileComponent()}
                                    {previewsComponent()}
                                </>
                            )}
                        </View>
                    )}

                    {fieldState.error && (
                        <MyAppText style={styles.error}>
                            {fieldState.error.message}
                        </MyAppText>
                    )}
                </View>
            )}
        />
    );
}

const stylesheet = createStyleSheet((theme) => ({
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        minHeight: 25,
    }),
    contentContainer: {
        flexDirection: 'column',
        gap: 12,
        marginTop: 8,
    },
    previewsContainer: {
        flexDirection: 'column',
        gap: 12,
    },
    dropzone: {
        flexDirection: 'column',
        gap: 8,
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: theme.colors.primary['500'],
        borderRadius: 8,
        padding: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        marginTop: 8,
        transition: 'all 0.2s ease',
    },
    dropzoneActive: {
        borderColor: theme.colors.primary['500'],
        backgroundColor: theme.colors.primary['200'],
    },
    dropzoneDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    uploadText: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500'],
    },
    dragDropText: {
        fontSize: 16,
        fontWeight: '400',
        color: theme.colors.neutral['400'],
    },
    supportedText: {
        marginTop: 8,
        fontSize: 12,
        color: theme.colors.neutral['500'],
    },
    recommendedText: {
        marginTop: 2,
        fontSize: 12,
        color: theme.colors.neutral['400'],
    },
    fileName: {
        fontSize: 14,
        color: theme.colors.textColor,
        fontWeight: '500',
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10,
        marginTop: 4,
    },
    previewContainer: {
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 45
    },
    previewImage: {
        width: 240,
        height: 120,
        borderRadius: 8,
    },
    removeButton: {
        backgroundColor: 'transparent',
        color: theme.colors.errors['300'],
        borderRadius: 20,
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    loadingContainer: {
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.neutral['100'],
        marginTop: 8,
    },
    loadingText: {
        marginTop: 12,
        fontSize: 14,
        color: theme.colors.neutral['500'],
    },
    addMoreButton: {
        flexDirection: 'row',
        gap: 8,
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: theme.colors.primary['200'],
        borderRadius: 8,
        padding: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        cursor: 'pointer',
    },
    addMoreText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.primary['500'],
    },
}));
